class ProductStickyATC {
  constructor() {
    this.stickyBar = null;
    this.mainSubmitButton = null;
    this.mainQuantityInput = null;
    this.stickyQuantityInput = null;
    this.stickySubmitButton = null;
    this.form = null;
    this.observer = null;
    this.sectionId = null;
    this.productId = null;
    this.hasSeenMainButton = false; // Track if user has seen main button
    this.isIntercomMessengerOpen = false;

    this.init();
  }

  init() {
    // Find the sticky bar element
    this.stickyBar = document.querySelector(".product-sticky-atc");
    if (!this.stickyBar) return;

    // Move sticky button to body to avoid stacking context issues
    this.moveStickyButtonToBody();

    this.sectionId = this.stickyBar.dataset.sectionId;
    this.productId = this.stickyBar.dataset.productId;
    this.isPreorderProduct = this.stickyBar.dataset.preorder === "true";
    const formId = this.stickyBar.dataset.formId;

    // Find related elements
    this.form = document.getElementById(formId);
    this.mainQuantityInput = document.getElementById(
      `Quantity-${this.sectionId}`
    );
    this.stickyQuantityInput = this.stickyBar.querySelector(".quantity__input");
    this.stickySubmitButton = this.stickyBar.querySelector(
      ".product-sticky-atc__submit"
    );
    this.mainSubmitButton = this.resolveMainSubmitButton();

    if (
      !this.form ||
      !this.mainSubmitButton ||
      !this.stickyQuantityInput ||
      !this.stickySubmitButton
    ) {
      // Globo Preorder may replace the CTA after load — retry briefly.
      this.scheduleMainButtonRetry();
      return;
    }

    this.bindAfterMainButtonReady();
  }

  isUsableCtaButton(button) {
    if (!button || button.nodeType !== 1) return false;
    if (button.classList.contains("gPreorderHidden")) return false;

    const style = window.getComputedStyle(button);
    if (style.display === "none" || style.visibility === "hidden") return false;

    // Prefer buttons that occupy layout space (Globo keeps a hidden original ATC).
    const rect = button.getBoundingClientRect();
    if (rect.height <= 0 && rect.width <= 0) return false;

    return true;
  }

  resolveMainSubmitButton() {
    if (!this.form && this.stickyBar) {
      const formId = this.stickyBar.dataset.formId;
      this.form = document.getElementById(formId);
    }

    const scope = this.form || document;
    const candidates = [
      ...scope.querySelectorAll(".gPreorderBtn, .gPreorderBtnLoaded"),
      document.getElementById(`ProductSubmitButton-${this.sectionId}`),
      ...scope.querySelectorAll(
        '.product-form__submit, button[type="submit"][name="add"], button[type="submit"]'
      ),
    ].filter(Boolean);

    const usable = candidates.find((button) => this.isUsableCtaButton(button));
    if (usable) return usable;

    // Mobile hides the in-page ATC cluster for sticky bar; still prefer a live CTA.
    const preferred = candidates.find(
      (button) => !button.classList.contains("gPreorderHidden")
    );
    return (
      preferred ||
      document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
      candidates[0] ||
      null
    );
  }

  scheduleMainButtonRetry() {
    if (this._mainButtonRetryId) return;
    let attempts = 0;
    this._mainButtonRetryId = window.setInterval(() => {
      attempts += 1;
      this.mainSubmitButton = this.resolveMainSubmitButton();
      if (this.mainSubmitButton && this.form && this.stickyQuantityInput && this.stickySubmitButton) {
        window.clearInterval(this._mainButtonRetryId);
        this._mainButtonRetryId = null;
        this.bindAfterMainButtonReady();
        return;
      }
      if (attempts >= 40) {
        window.clearInterval(this._mainButtonRetryId);
        this._mainButtonRetryId = null;
      }
    }, 250);
  }

  bindAfterMainButtonReady() {
    if (this._bound) return;
    this._bound = true;

    this.setupIntersectionObserver();
    this.setupEventListeners();
    this.setupScrollListener();
    this.setupDrawerListeners();
    this.setupChatListeners();
    this.setupFormSubmissionListeners();
    this.setupPreorderListeners();
    this.setupPreorderClickGate();
    this.syncQuantityInputs();
    this.setInitialScrollToTopPosition();
    this.updateAvailability();
    this.syncStickyVisibility();
  }

  refreshMainButtonBinding() {
    const nextButton = this.resolveMainSubmitButton();
    if (!nextButton) return false;

    const buttonChanged = nextButton !== this.mainSubmitButton;
    this.mainSubmitButton = nextButton;
    if (buttonChanged) {
      this.setupIntersectionObserver();
      this.setupVariantChangeListener();
    }
    this.isPreorderProduct =
      this.isPreorderProduct ||
      nextButton.classList.contains("gPreorderBtn") ||
      nextButton.classList.contains("gPreorderBtnLoaded");
    this.updateAvailability();
    this.syncStickyVisibility();
    return true;
  }

  setupPreorderListeners() {
    const refreshMainButton = () => {
      this.refreshMainButtonBinding();
    };

    document.addEventListener("globo.preorder.show.preorder", refreshMainButton);
    document.addEventListener("globo.preorder.show.addtocart", refreshMainButton);
  }

  moveStickyButtonToBody() {
    if (!this.stickyBar) return;

    // Store original parent for cleanup
    this.originalParent = this.stickyBar.parentNode;

    // Move sticky button to body
    document.body.appendChild(this.stickyBar);
  }

  setupIntersectionObserver() {
    if (!this.mainSubmitButton) return;

    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.hasSeenMainButton = true;
          }
          this.syncStickyVisibility();
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    this.observer.observe(this.mainSubmitButton);
  }

  setupEventListeners() {
    // Sticky submit button click
    this.stickySubmitButton.addEventListener("click", () => {
      this.submitForm();
    });

    // Quantity input sync - use 'change' event to avoid firing during stepUp/stepDown
    this.stickyQuantityInput.addEventListener("change", () => {
      if (this._isSyncing) {
        return;
      }
      this.syncToMainQuantity();
    });

    if (this.mainQuantityInput) {
      this.mainQuantityInput.addEventListener("change", () => {
        if (this._isSyncing) {
          return;
        }
        this.syncToStickyQuantity();
      });
    }

    // Listen for variant changes
    this.setupVariantChangeListener();
  }

  setupScrollListener() {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.syncStickyVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
  }

  setupDrawerListeners() {
    const drawerOpenEvents = [
      "cart:opened",
      "delivery:opened",
      "menu-drawer:open",
      "quick-view:opened",
    ];
    const drawerCloseEvents = [
      "cart:closed",
      "delivery:closed",
      "menu-drawer:close",
      "quick-view:closed",
    ];

    drawerOpenEvents.forEach((event) => {
      document.addEventListener(event, () => {
        this.hideStickyBar();
      });
    });

    drawerCloseEvents.forEach((event) => {
      document.addEventListener(event, () => {
        // Wait for body.drawer-open / scroll-lock to clear, then restore sticky.
        setTimeout(() => {
          this.syncStickyVisibility();
        }, 150);
      });
    });
  }

  setupChatListeners() {
    const bindIntercom = () => {
      if (typeof window.Intercom !== "function") return false;

      window.Intercom("onShow", () => {
        this.isIntercomMessengerOpen = true;
        this.syncStickyVisibility();
      });

      window.Intercom("onHide", () => {
        this.isIntercomMessengerOpen = false;
        this.syncStickyVisibility();
      });

      return true;
    };

    if (!bindIntercom()) {
      let attempts = 0;
      const retryId = window.setInterval(() => {
        attempts += 1;
        if (bindIntercom() || attempts >= 40) {
          window.clearInterval(retryId);
        }
      }, 250);
    }

    this.chatFrameObserver = new MutationObserver(() => {
      this.syncStickyVisibility();
    });

    const observeChatFrame = () => {
      const finFrame = document.querySelector(
        '[data-testid="fin-floating-frame"]'
      );
      if (!finFrame || finFrame.dataset.stickyAtcObserved === "true") return;

      finFrame.dataset.stickyAtcObserved = "true";
      this.chatFrameObserver.observe(finFrame, {
        attributes: true,
        attributeFilter: ["data-visibility-state", "style", "class"],
      });
      this.syncStickyVisibility();
    };

    observeChatFrame();

    this.chatDomObserver = new MutationObserver(() => {
      observeChatFrame();
    });

    if (document.body) {
      this.chatDomObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  isMainButtonInView() {
    if (!this.mainSubmitButton) return false;
    const rect = this.mainSubmitButton.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  isMainButtonAboveViewport() {
    if (!this.mainSubmitButton) return false;
    return this.mainSubmitButton.getBoundingClientRect().bottom < 0;
  }

  isAnyDrawerOpen() {
    return document.body.classList.contains("drawer-open");
  }

  isMessengerPanelOpen() {
    const finFrame = document.querySelector(
      '[data-testid="fin-floating-frame"]'
    );
    if (!finFrame) return false;
    // Collapsed launcher only — do not treat as open chat.
    if (finFrame.getAttribute("data-visibility-state") === "shrinked") {
      return false;
    }
    const computedStyle = window.getComputedStyle(finFrame);
    return (
      computedStyle.visibility !== "hidden" &&
      computedStyle.pointerEvents !== "none"
    );
  }

  isChatOpen() {
    if (this.isIntercomMessengerOpen) return true;
    return this.isMessengerPanelOpen();
  }

  /**
   * Keep sticky ATC in sync with drawer / chat state.
   * On mobile the sticky bar stays visible; hide only while drawer / chat is open.
   */
  syncStickyVisibility() {
    if (!this.stickyBar || !this.mainSubmitButton) return;

    const chatOpen = this.isChatOpen();
    document.documentElement.classList.toggle("product-chat-open", chatOpen);

    if (this.isAnyDrawerOpen() || chatOpen) {
      this.hideStickyBar();
      return;
    }

    this.updateAvailability();
  }

  setupVariantChangeListener() {
    // Watch for changes in the hidden variant ID input
    const variantInput = this.form.querySelector(".product-variant-id");
    if (variantInput && !this._variantObserver) {
      this._variantObserver = new MutationObserver(() => {
        this.updateAvailability();
        this.syncQuantityInputs();
      });
      this._variantObserver.observe(variantInput, { attributes: true });
    }

    // Watch for changes in submit button disabled state / Globo class / label
    if (this.mainSubmitButton) {
      if (this._submitObserver) {
        this._submitObserver.disconnect();
      }
      this._submitObserver = new MutationObserver(() => {
        this.updateAvailability();
      });
      this._submitObserver.observe(this.mainSubmitButton, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  syncQuantityInputs() {
    if (this.mainQuantityInput && this.stickyQuantityInput) {
      const mainValue = this.mainQuantityInput.value;
      const stickyValue = this.stickyQuantityInput.value;

      if (mainValue !== stickyValue) {
        this.stickyQuantityInput.value = mainValue;
      }
    }
  }

  syncToMainQuantity() {
    if (this.mainQuantityInput && this.stickyQuantityInput) {
      const newValue = this.stickyQuantityInput.value;

      // Delay sync to avoid chain reaction
      setTimeout(() => {
        this.mainQuantityInput.value = newValue;
      }, 0);
    }
  }

  syncToStickyQuantity() {
    if (this.mainQuantityInput && this.stickyQuantityInput) {
      const newValue = this.mainQuantityInput.value;

      // Delay sync to avoid chain reaction
      setTimeout(() => {
        this.stickyQuantityInput.value = newValue;
      }, 0);
    }
  }

  isPreorderLabel(text) {
    return !!text && /pre[\s-]?order/i.test(String(text));
  }

  /**
   * Globo has converted the live CTA to Pre-order (class + label).
   * Product tag alone is not enough — primary + sticky stay locked until then.
   */
  isGloboPreorderReady() {
    if (!this.mainSubmitButton || !this.mainSubmitButton.classList) return false;
    if (this.mainSubmitButton.classList.contains("gPreorderHidden")) return false;

    const hasPreorderClass =
      this.mainSubmitButton.classList.contains("gPreorderBtn") ||
      this.mainSubmitButton.classList.contains("gPreorderBtnLoaded");
    if (!hasPreorderClass) return false;

    return this.isPreorderLabel(this.getMainButtonLabel());
  }

  getButtonLabelText(button) {
    if (!button) return "";
    const textEl =
      button.querySelector(".btn__text") ||
      button.querySelector(".product-card__atc-text, .product-card__action-text");
    return ((textEl || button).textContent || "").replace(/\s+/g, " ").trim();
  }

  /**
   * Click-time gate only: block ATC until Globo shows Pre-order.
   * No MutationObserver / no disabled toggles on the primary button.
   */
  setupPreorderClickGate() {
    if (!this.isPreorderProduct || !this.form || this._preorderGateBound) return;
    this._preorderGateBound = true;

    this.form.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest(
          'button[type="submit"], .product-form__submit, .gPreorderBtn, .gPreorderBtnLoaded'
        );
        if (!button || !this.form.contains(button)) return;
        if (this.isGloboPreorderReady()) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    this.form.addEventListener(
      "submit",
      (event) => {
        if (this.isGloboPreorderReady()) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  isMainButtonDisabled() {
    if (!this.mainSubmitButton) return true;
    if (this.mainSubmitButton.getAttribute("aria-disabled") === "true") {
      return true;
    }
    return !!this.mainSubmitButton.disabled;
  }

  getMainButtonLabel() {
    if (!this.mainSubmitButton) return "";
    const textEl =
      this.mainSubmitButton.querySelector(".btn__text") ||
      this.mainSubmitButton.querySelector(
        ".product-card__atc-text, .product-card__action-text"
      );
    const raw = ((textEl || this.mainSubmitButton).textContent || "")
      .replace(/\s+/g, " ")
      .trim();
    return raw;
  }

  getStickyButtonLabel(isDisabled, preorderReady) {
    if (preorderReady) {
      return "Pre-order";
    }

    if (this.isPreorderProduct) {
      // Wait for Globo — keep Add to cart label and stay non-clickable.
      return this.getTranslation("products.product.add_to_cart");
    }

    if (isDisabled) {
      return this.getTranslation("products.product.sold_out");
    }

    return this.getTranslation("products.product.add_to_cart");
  }

  updateAvailability() {
    if (!this.stickyBar) return;

    this.mainSubmitButton = this.resolveMainSubmitButton() || this.mainSubmitButton;
    if (!this.mainSubmitButton) return;

    if (this.isAnyDrawerOpen() || this.isChatOpen()) {
      this.hideStickyBar();
      return;
    }

    const isDisabled = this.isMainButtonDisabled();
    const preorderReady = this.isGloboPreorderReady();

    // Sticky stays non-clickable until Globo shows Pre-order (sticky button only).
    let stickyDisabled = isDisabled;
    if (this.isPreorderProduct) {
      stickyDisabled = !preorderReady;
    }

    this.showStickyBar();

    this.stickySubmitButton.disabled = stickyDisabled;

    const buttonText = this.stickySubmitButton.querySelector(".btn__text");
    if (buttonText) {
      buttonText.textContent = this.getStickyButtonLabel(isDisabled, preorderReady);
    }
  }

  showStickyBar() {
    if (!this.stickyBar) return;

    this.stickyBar.style.display = "block";
    // Use requestAnimationFrame to ensure the display change is processed
    requestAnimationFrame(() => {
      this.stickyBar.classList.add("is-visible");
      this.applyFloatingButtonsOffset();
    });
  }

  hideStickyBar() {
    if (!this.stickyBar) return;

    this.stickyBar.classList.remove("is-visible");
    this.resetFloatingButtonsOffset();
    // Hide after animation completes
    setTimeout(() => {
      if (!this.stickyBar.classList.contains("is-visible")) {
        this.stickyBar.style.display = "none";
      }
    }, 300);
  }

  setupFormSubmissionListeners() {
    // Listen for form submission events to sync loading states
    this.form.addEventListener("submit", () => {
      this.stickySubmitButton.classList.add("btn--loading");
      this.stickySubmitButton.disabled = true;
    });

    // Listen for form completion events
    document.addEventListener("product-ajax:added", () => {
      this.resetStickyButtonState();
    });

    document.addEventListener("product-ajax:error", () => {
      this.resetStickyButtonState();
    });
  }

  resetStickyButtonState() {
    this.stickySubmitButton.classList.remove("btn--loading");
    this.updateAvailability();
  }

  submitForm() {
    if (!this.form || this.stickySubmitButton.disabled) return;

    this.mainSubmitButton = this.resolveMainSubmitButton() || this.mainSubmitButton;

    if (this.isPreorderProduct && !this.isGloboPreorderReady()) {
      return;
    }

    // Prefer clicking the live main CTA so Globo Pre-order handlers run.
    if (
      this.mainSubmitButton &&
      typeof this.mainSubmitButton.click === "function" &&
      !this.mainSubmitButton.disabled
    ) {
      this.mainSubmitButton.click();
      return;
    }

    this.form.requestSubmit();
  }

  getChatButtons() {
    return document.querySelectorAll(
      [
        "#cedora-spotlight-chat-fab",
        ".cxgenie-chat-button",
        ".intercom-lightweight-app-launcher",
        ".intercom-launcher",
      ].join(", ")
    );
  }

  /**
   * Bottom-fixed promos covered by sticky ATC (Klaviyo teaser, BOGOS Spend More toast/icon).
   * CSS on html.product-sticky-atc-visible also lifts late-injected matches.
   */
  getPromoFloatingElements() {
    return document.querySelectorAll(
      [
        '[class*="kl-teaser"]',
        "#bogos-to-widget-icon-wrap:not([class*=\"middle\"])",
        "#bogos-to-widget-iframe.sca-show:not(.bogos-to-modal):not([class*=\"middle\"])",
        // Spend More Save More congrats toast ("Great!")
        "#bogos-cart-discount-congrats-msg-wrap",
      ].join(", ")
    );
  }

  getFloatingOffsetElements() {
    return [
      ...this.getChatButtons(),
      ...this.getPromoFloatingElements(),
    ];
  }

  applyFloatingButtonsOffset() {
    const floatingElements = this.getFloatingOffsetElements();
    const chats = this.getChatButtons();
    const toTop = document.querySelector(".scroll-to-top");

    if (!this.stickyBar) return;

    // Sticky height + gap so floating widgets clear the ATC bar.
    const stickyHeight = this.stickyBar.getBoundingClientRect().height;
    const offsetFromSticky = stickyHeight + 12;
    document.documentElement.style.setProperty(
      "--product-sticky-atc-offset",
      `${offsetFromSticky}px`
    );
    document.documentElement.classList.add("product-sticky-atc-visible");

    // Dismiss expanded Spotlight home + block frame hit-testing over ATC.
    this.dismissOverlappingChatPanel();
    this.blockChatFramePointerEvents();

    floatingElements.forEach((element) => {
      element.style.transition = "transform 0.28s ease, bottom 0.28s ease";
      element.style.transform = `translateY(-${offsetFromSticky}px)`;
      element.style.pointerEvents = "auto";
    });

    if (toTop) {
      // Position to-top button: sticky height + gap + chat height + gap
      const primaryChat = chats[0];
      const chatHeight = primaryChat
        ? primaryChat.getBoundingClientRect().height
        : 48;
      const toTopOffset = offsetFromSticky + chatHeight + 32;

      toTop.style.transition =
        "bottom 0.28s ease, opacity 0.2s ease, transform 0.28s ease";
      toTop.style.bottom = `${toTopOffset}px`;
    }
  }

  getChatFrameElements() {
    return document.querySelectorAll(
      [
        '[data-testid="fin-floating-frame"]',
        ".intercom-lightweight-app",
        "#intercom-container",
        ".intercom-namespace",
        'iframe[name="intercom-messenger-frame"]',
        'iframe[name="intercom-launcher-frame"]',
        ".intercom-lightweight-app-launcher",
        ".intercom-launcher",
      ].join(", ")
    );
  }

  blockChatFramePointerEvents() {
    this.getChatFrameElements().forEach((element) => {
      element.style.pointerEvents = "none";
    });
  }

  resetChatFramePointerEvents() {
    this.getChatFrameElements().forEach((element) => {
      element.style.pointerEvents = "";
    });
  }

  dismissOverlappingChatPanel() {
    const finFrame = document.querySelector(
      '[data-testid="fin-floating-frame"]'
    );
    if (!finFrame) return;

    const state = finFrame.getAttribute("data-visibility-state");
    // Leave the collapsed launcher alone; only dismiss expanded Spotlight home.
    if (state === "shrinked") return;

    if (typeof window.Intercom === "function") {
      try {
        window.Intercom("hide");
      } catch (error) {
        // Ignore Intercom hide errors
      }
    }
  }

  resetFloatingButtonsOffset() {
    const floatingElements = this.getFloatingOffsetElements();
    const toTop = document.querySelector(".scroll-to-top");

    document.documentElement.classList.remove("product-sticky-atc-visible");
    document.documentElement.style.removeProperty("--product-sticky-atc-offset");
    this.resetChatFramePointerEvents();

    floatingElements.forEach((element) => {
      element.style.transform = "";
      element.style.pointerEvents = "";
    });

    if (toTop) {
      // Reset to initial position: 32px above chat button
      this.setInitialScrollToTopPosition();
    }
  }

  setInitialScrollToTopPosition() {
    const chat = this.getChatButtons()[0];
    const toTop = document.querySelector(".scroll-to-top");

    if (chat && toTop) {
      const chatHeight = chat.getBoundingClientRect().height;
      const chatBottom = parseInt(window.getComputedStyle(chat).bottom) || 16;
      const toTopOffset = chatBottom + chatHeight + 32; // 32px gap above chat

      toTop.style.transition =
        "bottom 0.28s ease, opacity 0.2s ease, transform 0.28s ease";
      toTop.style.bottom = `${toTopOffset}px`;
    }
  }

  getTranslation(key) {
    // Simple translation fallback - in a real implementation,
    // you might want to use Shopify's translation system
    const translations = {
      "products.product.sold_out": "Sold out",
      "products.product.add_to_cart": "Add to cart",
    };
    return translations[key] || key;
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.chatFrameObserver) {
      this.chatFrameObserver.disconnect();
    }
    if (this.chatDomObserver) {
      this.chatDomObserver.disconnect();
    }
    if (this._submitObserver) {
      this._submitObserver.disconnect();
    }
    if (this._variantObserver) {
      this._variantObserver.disconnect();
    }
    if (this._mainButtonRetryId) {
      window.clearInterval(this._mainButtonRetryId);
      this._mainButtonRetryId = null;
    }

    // Move sticky button back to original parent
    if (this.stickyBar && this.originalParent) {
      this.originalParent.appendChild(this.stickyBar);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new ProductStickyATC();
});

// Also initialize for dynamic content (like AJAX-loaded product pages)
document.addEventListener("shopify:section:load", () => {
  new ProductStickyATC();
});
